import {useFeatureAppEnv, useSalesChannel} from '@smart/feature-app/react';
import {useQueryParams} from '@smart/react-utils';
import {makeCancelable} from '@smart/utils';
import type {ReactNode} from 'react';
import React, {createContext, useContext} from 'react';
import type {
  ProductSearchProduct,
  ProductSearchResult,
} from '../../../bff/product-search-model/create-product-search-model';
import type {Banner} from '../../../bff/types';
import {Sorting} from '../../../bff/types';
import type {FacetOption} from '../../components/facet-panel';
import {useLocale} from '../../hooks/use-locale';
import {
  areArraysEqual,
  facetReducer,
  fetchSearchModel,
  getSearch,
} from '../../utils';
import {
  convertFacetQueryToParams,
  convertParamsToQuery,
} from '../../utils/query-param.utils';
import {useAppModel} from '../app-model-context';

type ProductSearchState = {
  loading: boolean;
  initialLoading: boolean;
  hasMore: boolean;
  currentPage: number;
  products: ProductSearchResult['products'];
  newestProducts: ProductSearchResult['products'];
  sortBy: Sorting;
  error?: Error | null;
};

const initialProductSearchState: ProductSearchState = {
  initialLoading: true,
  loading: true,
  hasMore: false,
  currentPage: 0,
  products: [],
  newestProducts: [],
  sortBy: Sorting.Relevance,
  error: null,
};

export type ProductSearchContext = ProductSearchState & {
  activeFilters: FacetOption[];
  facets: ProductSearchResult['facets'];
  setSortBy: (sortBy: Sorting) => void;
  resetFilters: () => void;
  toggleFilterOption: (
    option: Pick<FacetOption, 'facetId' | 'id' | 'selected'>,
  ) => void;
  applyFilter: (option: {
    facets: {facetId: string; optionId: string; selected: boolean}[];
  }) => void;
  toggleFilter: (facetId: string) => void;
  loadMore: () => void;
};

const notImplemented = () => {
  throw new Error(`Not implemented`);
};

const initialProductSearchContext: ProductSearchContext = {
  ...initialProductSearchState,
  activeFilters: [],
  facets: [],
  setSortBy: notImplemented,
  resetFilters: notImplemented,
  toggleFilterOption: notImplemented,
  applyFilter: notImplemented,
  toggleFilter: notImplemented,
  loadMore: notImplemented,
};

const ProductSearchContext = createContext<ProductSearchContext>(
  initialProductSearchContext,
);

interface ProductSearchProviderProps {
  children: ReactNode;
}

const mergeBanner = (
  productList: (ProductSearchProduct | Banner)[],
  bannerList: Banner[],
  hasMore: boolean,
): (ProductSearchProduct | Banner)[] => {
  const productsSet = new Set(productList);
  bannerList = bannerList
    .filter((item) => {
      return !(
        item.linkDestination &&
        item.linkLabel &&
        item.linkDestination.__typename === `ParameterizedPageModel` &&
        areArraysEqual(
          getSearch(window.location.search, `url`),
          getSearch(item.linkDestination.queryParams, `parameterizedPage`),
        )
      );
    })
    .sort((a, b) => a.position - b.position);

  for (const banner of bannerList) {
    if (banner.position && banner.position > 0) {
      let index;
      if (banner.position > productList.length && !hasMore) {
        index = productList.length;
      } else if (banner.position <= productList.length) {
        index = banner.position - 1;
      } else {
        continue;
      }
      if (!productsSet.has(banner)) {
        productList.splice(index, 0, banner);
      }
    }
  }
  return productList;
};

export const ProductSearchProvider = ({
  children,
}: ProductSearchProviderProps) => {
  const {
    baseUrl,
    featureServices: {
      'endpoint-directory': endpointDirectory,
      'payment-config-service': paymentConfigService,
    },
  } = useFeatureAppEnv();

  const salesChannel = useSalesChannel();
  const {marketId, language} = useLocale();
  const {appModel} = useAppModel();

  const {urlSearchParams, setQueryParams} = useQueryParams();

  const [state, setState] = React.useState(initialProductSearchState);
  const [{facets, activeFilters}, dispatch] = React.useReducer(facetReducer, {
    facets: [],
    activeFilters: [],
  });

  const [paymentConfig, setPaymentConfig] = React.useState(
    paymentConfigService.getConfig(),
  );

  const cancelRef = React.useRef<() => void>();

  const query = React.useMemo(() => {
    return state.initialLoading
      ? convertParamsToQuery(urlSearchParams)
      : activeFilters.map((filter) => filter.value).join(`:`);
  }, [activeFilters, urlSearchParams, state.initialLoading]);

  const getCancelableSearchModel = React.useCallback(
    async (currentPage: number = 0): Promise<() => void> => {
      const {promise: searchModelPromise, cancel} = makeCancelable(
        fetchSearchModel(baseUrl, {
          endpointDirectory,
          marketId,
          language,
          paymentConfig,
          query: state.initialLoading ? `` : query,
          sortBy: state.sortBy,
          salesChannel,
          currentPage,
        }),
      );

      try {
        setState((data) => ({...data, loading: true}));
        const {
          facets: _facets,
          products,
          hasMore,
          currentPage: newPage,
        } = await searchModelPromise;

        dispatch({type: `update-facets`, payload: {facets: _facets}});

        if (state.initialLoading) {
          for (const [key, v] of urlSearchParams.entries()) {
            const facet = _facets.find((f) => f.id === key);
            const option = facet?.options.find((o) =>
              decodeURIComponent(o.value).includes(`${key}:${v}`),
            );

            if (!option) continue;
            toggleFilterOption({
              facetId: option.facetId,
              id: option.id,
              selected: true,
            });
          }
        }
        setState((data) => {
          const productList = mergeBanner(
            currentPage === 0 ? products : [...data.products, ...products],
            appModel?.bannerList ?? [],
            hasMore,
          );
          return {
            ...data,
            initialLoading: false,
            loading: false,
            products: productList,
            newestProducts: products,
            hasMore,
            currentPage: newPage,
            error: null,
          };
        });
      } catch (error) {
        setState((data) => ({
          ...data,
          initialLoading: false,
          loading: false,
          products: [],
          newestProducts: [],
          error: error instanceof Error ? error : new Error(`Unknown error`),
        }));
      }

      return cancel;
    },
    [
      baseUrl,
      endpointDirectory,
      marketId,
      language,
      paymentConfig,
      state.initialLoading,
      state.sortBy,
      query,
      salesChannel,
      urlSearchParams,
      appModel?.bannerList,
    ],
  );

  React.useEffect(() => {
    paymentConfigService.subscribe(() => {
      setPaymentConfig(paymentConfigService.getConfig());
    });
  }, [paymentConfigService]);

  React.useEffect(() => {
    if (state.initialLoading) return;

    // When facet query is updated, update URL search params,
    // reset products and current page

    // update query params to match the facets
    const queryParams = convertFacetQueryToParams(query);
    setQueryParams(queryParams, {replaceParams: true});

    setState((data) => ({
      ...data,
      products: [],
      newestProducts: [],
      currentPage: 0,
    }));
  }, [
    query,
    urlSearchParams,
    setQueryParams,
    state.initialLoading,
    paymentConfig,
  ]);

  React.useEffect(() => {
    setState((data) => ({...data, loading: true}));

    void (async () => {
      const cancel = await getCancelableSearchModel();
      cancelRef.current = cancel;
    })();

    return cancelRef.current?.();
  }, [getCancelableSearchModel]);

  const loadMore = React.useCallback(async () => {
    const cancel = await getCancelableSearchModel(state.currentPage + 1);
    cancelRef.current = cancel;
  }, [getCancelableSearchModel, state.currentPage]);

  const setSortBy = (sortBy: Sorting) => {
    setState((data) => ({
      ...data,
      sortBy,
      loading: true,
      products: [],
      newestProducts: [],
      currentPage: 0,
    }));
  };

  const resetFilters = () => dispatch({type: `unselected-all-facet-options`});

  const toggleFilterOption = (
    option: Pick<FacetOption, 'facetId' | 'id' | 'selected'>,
  ) => {
    dispatch({
      type: `toggle-facet-option`,
      payload: {
        facetId: option.facetId,
        optionId: option.id,
        selected: option.selected,
      },
    });
  };

  const applyFilter = (option: {
    facets: {facetId: string; optionId: string; selected: boolean}[];
  }) => {
    dispatch({
      type: `apply-filter`,
      payload: {
        facets: option.facets,
      },
    });
  };

  const toggleFilter = (facetId: string) => {
    dispatch({
      type: `toggle-facet`,
      payload: {id: facetId},
    });
  };

  const value: ProductSearchContext = {
    ...state,
    facets,
    activeFilters,
    loadMore,
    setSortBy,
    toggleFilter,
    toggleFilterOption,
    resetFilters,
    applyFilter,
  };

  return (
    <ProductSearchContext.Provider value={value}>
      {children}
    </ProductSearchContext.Provider>
  );
};

export const useProductSearch = () => {
  const context = useContext(ProductSearchContext);
  if (!context) {
    throw new Error(
      `useProductSearch must be used within a ProductSearchProvider`,
    );
  }
  return context;
};
